import { getDateFnsLocale } from '@/src/shared/application/translation.service'
import { addDays, endOfMonth, endOfQuarter, format, isAfter, isBefore, isSameDay, startOfDay, startOfMonth, startOfQuarter } from 'date-fns'
import { GranularityEnum } from '../enums/granularity.enum'

export const formatDateByGranularity = (date: Date, granularity: GranularityEnum | '', language?: string): string => {
  const locale = getDateFnsLocale(language)
  switch (granularity) {
    case GranularityEnum.FIFTEEN_MINUTES:
      return format(date, 'EEEE, dd. LLLL yyyy HH:mm', { locale }) // 24 March 2024 00:00
    case GranularityEnum.THIRTY_MINUTES:
      return format(date, 'dd. LLLL yyyy HH:mm', { locale }) // 24 March 2024 00:00
    case GranularityEnum.ONE_HOUR:
      return format(date, 'dd. LLLL yyyy HH:mm', { locale }) // 24 March 2024 00:00
    case GranularityEnum.ONE_DAY:
      return format(date, 'EEEE, dd. LLLL yyyy', { locale }) // Montag, 24. Mai 2024
    case GranularityEnum.ONE_WEEK:
      return format(date, 'dd. LLLL yyyy', { locale }) // 24 March 2024
    case GranularityEnum.ONE_MONTH:
      return format(date, 'MMMM yyyy', { locale }) // March 2024
    case GranularityEnum.MINUTES:
      return format(date, 'dd. LLLL yyyy HH:mm', { locale }) // 24 March 2024 00:00
    default:
      return format(date, 'dd.MM.yyyy', { locale }) // 24.03.2024
  }
}

export const formatDateToDayMonthYear = (date: Date, language?: string): string => {
  const locale = getDateFnsLocale(language)
  return format(date, 'dd.MM.yyyy', { locale })
}

export const daysBetweenDates = (date1: Date, date2: Date): number => {
  const timeDifference = Math.abs(date2.getTime() - date1.getTime())

  return Math.ceil(timeDifference / (1000 * 3600 * 24))
}

export const getEndOfYear = (): Date => {
  const year = new Date().getFullYear()
  return new Date(year, 11, 31, 23, 59, 59, 999)
}

export const getEndOfLastYear = (): Date => {
  const year = new Date().getFullYear() - 1
  return new Date(year, 11, 31, 23, 59, 59, 999)
}

// Parses an ISO-8601 timestamp using its wall-clock components, ignoring any offset.
// e.g. "2026-05-19T00:00:00+01:00" to "local Date for 2026-05-19 00:00:00", regardless of browser TZ.
// Use when the backend's offset represents the bucket's intended local time and you don't want
// `new Date(iso)` to shift the day/hour into the browser's zone.
export function parseIsoAsWallClock(iso: string): Date {
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?/)
  if (!match) return new Date(iso)
  const [, y, mo, d, h, mi, s, ms] = match
  return new Date(+y, +mo - 1, +d, +h, +mi, +s, ms ? +ms.padEnd(3, '0') : 0)
}

export function stripTimezone(date) {
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()
  const hours = date.getHours()
  const minutes = date.getMinutes()
  const seconds = date.getSeconds()
  const milliseconds = date.getMilliseconds()
  return new Date(year, month, day, hours, minutes, seconds, milliseconds)
}

export function getGermanMonthName(month: number): string {
  const germanMonths = [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
  ]

  if (month < 0 || month > 11) {
    throw new Error('Invalid month number. Must be between 1 and 12.')
  }

  return germanMonths[month]
}

export const formatDateToLiteral = (date: Date, currentLanguage: string): string => {
  return date.toLocaleDateString(currentLanguage, { day: 'numeric', month: 'long' })
}
export const formatDateToYearMonthDay = (date: Date, currentLanguage: string): string => {
  return date.toLocaleDateString(currentLanguage, { day: 'numeric', month: 'numeric', year: 'numeric' })
}

export const generateTimeOptions = (stepMinutes = 15): { value: string; label: string }[] => {
  const step = Number.isFinite(stepMinutes) && stepMinutes > 0 ? Math.floor(stepMinutes) : 15
  const times: { value: string; label: string }[] = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += step) {
      const hh = String(h).padStart(2, '0')
      const mm = String(m).padStart(2, '0')
      const time = `${hh}:${mm}`
      times.push({ value: time, label: time })
    }
  }
  return times
}

export const checkDatesOverlap = (queryDateInterval: [Date, Date], targetDateInterval: [Date, Date]) => {
  const startIsSameInitialTargetDay = isSameDay(queryDateInterval[0], targetDateInterval[0])
  const startIsAfterInitialTargetDay = isAfter(queryDateInterval[0], targetDateInterval[0])
  const startIsSameFinalTargetDay = isSameDay(queryDateInterval[0], targetDateInterval[1])
  const startIsBeforeFinalTargetDay = isBefore(queryDateInterval[0], targetDateInterval[1])

  const endIsSameInitialTargetDay = isSameDay(queryDateInterval[1], targetDateInterval[0])
  const endIsAfterInitialTargetDay = isAfter(queryDateInterval[1], targetDateInterval[0])
  const endIsSameFinalTargetDay = isSameDay(queryDateInterval[1], targetDateInterval[1])
  const endIsBeforeFinalTargetDay = isBefore(queryDateInterval[1], targetDateInterval[1])

  const startIsBeforeInitialTargetDay = isBefore(queryDateInterval[0], targetDateInterval[0])
  const endIsAfterFinalTargetDay = isAfter(queryDateInterval[1], targetDateInterval[1])

  const startWithinTargetIntervalRange =
    (startIsSameInitialTargetDay || startIsAfterInitialTargetDay) && (startIsSameFinalTargetDay || startIsBeforeFinalTargetDay)
  const endWithinTargetIntervalRange =
    (endIsSameInitialTargetDay || endIsAfterInitialTargetDay) && (endIsSameFinalTargetDay || endIsBeforeFinalTargetDay)
  const possiblyOnlyStartWithinTargetIntervalRange =
    startWithinTargetIntervalRange && (endWithinTargetIntervalRange || !endWithinTargetIntervalRange)
  const possiblyOnlyEndWithinTargetIntervalRange =
    endWithinTargetIntervalRange && (startWithinTargetIntervalRange || !startWithinTargetIntervalRange)
  const startAndEndCoverTargetIntervalRange =
    (startIsBeforeInitialTargetDay || startIsSameInitialTargetDay) && (endIsSameFinalTargetDay || endIsAfterFinalTargetDay)

  return (
    startWithinTargetIntervalRange ||
    endWithinTargetIntervalRange ||
    (possiblyOnlyStartWithinTargetIntervalRange && possiblyOnlyEndWithinTargetIntervalRange) ||
    startAndEndCoverTargetIntervalRange
  )
}

export const getYearMonthFromDateString = (dateStr: string): string | null => {
  if (!dateStr || dateStr.length < 7) return null
  return dateStr.substring(0, 7)
}

/**
 * Helps with the cases of 2025-04-22T21:59:59.999Z being sent when users selects 2025-04-22 as the date range
 * when the backend expects an "exclusive" end
 */
export const snapToNextDay = (dateStr: string): Date => {
  const date = new Date(dateStr)
  if (date.getHours() > 12) {
    return startOfDay(addDays(date, 1))
  }
  return date
}

export const getCurrentQuarterOfYearRange = (year: number): [Date, Date] => {
  const current = new Date()
  const date = new Date(year, current.getMonth(), current.getDate())
  return [startOfQuarter(date), endOfQuarter(date)]
}

export const getCurrentMonthOfYearRange = (year: number): [Date, Date] => {
  const current = new Date()
  const date = new Date(year, current.getMonth(), current.getDate())
  return [startOfMonth(date), endOfMonth(date)]
}

/**
 * Given a tz string ('Europe/Berlin'), we figure out whats the minute offset from GMT.
 */
export const getOffsetMinutes = (tz: string): number => {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: tz,
    timeZoneName: 'shortOffset',
  }).formatToParts(new Date())

  const tzPart = parts.find(p => p.type === 'timeZoneName')?.value ?? 'GMT+1'
  const match = tzPart.match(/GMT([+-])(\d+)(?::(\d+))?/)
  if (!match) return 60
  const sign = match[1] === '+' ? 1 : -1
  return sign * (Number.parseInt(match[2], 10) * 60 + Number.parseInt(match[3] ?? '0', 10))
}
